<template>
  <main class="grow seo-page" :class="{ 'is-story': story }" ref="pageRoot">
    <section class="seo-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="seo-hero-grid">
          <div class="min-w-0">
            <h1>{{ story?.heroTitle || page.h1 }}</h1>
            <p v-if="page.answerSummary" class="seo-answer">
              <template v-for="(part, index) in answerParts" :key="index">
                <NuxtLink v-if="part.path" :to="localePath(part.path)">{{ part.text }}</NuxtLink>
                <template v-else>{{ part.text }}</template>
              </template>
            </p>
            <!-- Story pages keep the hero to one short promise and a plan line. -->
            <p class="seo-lede">{{ story ? story.heroCopy || page.lede : ledeParts.hero }}</p>
            <p v-if="story?.heroPlanNote" class="seo-plan-note">{{ story.heroPlanNote }}</p>

            <div class="seo-actions">
              <a
                :href="addReferralToUrl(story ? storyTrialUrl(story) : 'https://app.workspace369.com/signup')"
                class="seo-primary"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${page.badge} primary CTA`"
                :data-conversion-location="page.slug"
              >
                Start free trial
              </a>
              <!-- Trial terms sit directly under the trial button on phones (legal B1.1); wider screens put them on their own line under the row. -->
              <TrialDisclosure centered :plan="storyTrialPlan(story)" />
              <a
                v-if="teamOffer"
                href="#team-pricing"
                class="seo-secondary"
                data-conversion-event="pricing_click"
                :data-conversion-label="`${page.badge} team pricing`"
                :data-conversion-location="page.slug"
              >
                See team pricing
              </a>
              <a
                v-else-if="freelancerPlan"
                href="#solo-pricing"
                class="seo-secondary"
                data-conversion-event="pricing_click"
                :data-conversion-label="`${page.badge} solo pricing`"
                :data-conversion-location="page.slug"
              >
                See plans
              </a>
              <NuxtLink
                v-else
                :to="localePath('/contact-sales')"
                class="seo-secondary"
                data-conversion-event="demo_request_click"
                :data-conversion-label="`${page.badge} sales CTA`"
                :data-conversion-location="page.slug"
              >
                Talk to sales
              </NuxtLink>
            </div>

            <div v-if="!story" class="seo-stats" aria-label="Workspace369 highlights">
              <div v-for="stat in page.stats" :key="stat.label">
                <strong>{{ stat.value }}</strong>
                <span>{{ stat.label }}</span>
              </div>
            </div>
          </div>

          <!-- A page with `heroScene: "booking"` shows its booking page playing (StoryBookingStage,
               sample `bookingSample`); story pages (data/feature-stories.ts) show their hero scene.
               Either replaces the screenshot. -->
          <div v-if="page.heroScene === 'booking'" class="seo-hero-visual">
            <LazyStoryBookingStage :sample="page.bookingSample" autoplay tall :label="page.heroSceneLabel" />
          </div>
          <!-- App-screen heroes (story.hero.replica with `app`): the real app screen at its own scale,
               played once by StoryReplicaStage, as on FeatureDetailPage. -->
          <div v-else-if="story?.hero?.replica" class="seo-hero-visual seo-hero-story" :class="{ 'seo-hero-story--app': story.hero.app }">
            <LazyStoryReplicaStage
              class="feature-hero-frame"
              :replica="story.hero.replica"
              :app="story.hero.app"
              :states="story.hero.states"
              :holds="story.hero.holds"
              :label="story.hero.label"
              autoplay
            />
          </div>
          <div v-else-if="story" class="seo-hero-visual seo-hero-story">
            <LazyStoryHero :hero="story.hero" :label="story.heroLabel" />
          </div>
          <div v-else class="seo-hero-visual">
            <div class="seo-frame">
              <div class="seo-frame-bar" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
                <strong>workspace369.com</strong>
              </div>
              <img
                :src="heroVisual.src"
                :alt="heroVisual.alt"
                width="1600"
                :height="heroVisual.height"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Story pages skip the key facts, the on-page nav, the reviews, the mid-page band and the
         scenario cards: the chapters already carry them, and each repeated the one before. -->
    <section v-if="!story" class="facts-band">
      <div class="mx-auto max-w-6xl px-4 py-10 sm:px-6 md:py-12">
        <p v-if="ledeParts.rest" class="facts-intro">{{ ledeParts.rest }}</p>
        <p class="facts-eyebrow">Key facts</p>
        <div class="facts-grid">
          <p v-for="fact in pageFacts" :key="fact" class="fact-line">{{ fact }}</p>
          <p class="fact-line">
            Workspace369 starts at ${{ planFacts.cadetMonthly }}/mo for {{ planFacts.cadetSeats }} seat, and the {{ planFacts.topName }} plan includes {{ planFacts.topSeats }} seats and {{ planFacts.topUsage }} at ${{ planFacts.topMonthly }}/mo.
          </p>
          <p class="fact-line">
            Additional seats cost $10 each; full pricing is public on the
            <NuxtLink :to="localePath('/pricing')">Workspace369 pricing page</NuxtLink>.
          </p>
        </div>
      </div>
    </section>

    <section v-if="page.comparison" id="compared" class="section-light comparison-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
        <div class="section-heading">
          <h2>{{ page.comparison.title }}</h2>
        </div>
        <p class="comparison-answer">{{ page.comparison.answer }}</p>
        <div class="comparison-scroll" role="region" :aria-label="page.comparison.title" tabindex="0">
          <table class="comparison-table">
            <thead>
              <tr>
                <th v-for="column in page.comparison.columns" :key="column" scope="col">{{ column }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in page.comparison.rows" :key="row[0]" :class="{ 'comparison-ours': row[0] === 'Workspace369' }">
                <th scope="row">{{ row[0] }}</th>
                <td v-for="(cell, i) in row.slice(1)" :key="i">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="comparison-source">
          {{ page.comparison.source }}
          <NuxtLink v-if="page.comparison.guidePath" :to="localePath(page.comparison.guidePath)">{{ page.comparison.guideLabel }}</NuxtLink>
        </p>
      </div>
    </section>

    <nav v-if="!story" class="page-nav" aria-label="On this page">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="page-nav-inner">
          <span>On this page</span>
          <a v-if="page.comparison" href="#compared">Compared</a>
          <a v-if="story" href="#how-it-works">How it works</a>
          <template v-else>
            <a href="#the-problem">The problem</a>
            <a href="#the-product">The product</a>
            <a href="#workflow">How it works</a>
            <a v-if="page.setupGuide" href="#setup">Set up</a>
            <a v-if="page.decision" href="#options">Options compared</a>
            <a href="#coverage">Coverage</a>
          </template>
          <a href="#faq">FAQ</a>
        </div>
      </div>
    </nav>

    <section v-if="isFeaturePage" id="what-is" class="feature-definition-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="feature-definition-layout">
          <div>
            <h2>{{ page.definitionQuestion }}</h2>
          </div>
          <div>
            <p class="feature-definition-answer">{{ page.definition }}</p>
            <div class="feature-source-line">
              <span>Product reference</span>
              <p>
                Reviewed July 17, 2026 by the Workspace369 Product Team. Availability can
                vary by plan, region, and workspace configuration.
              </p>
              <a
                v-if="page.docsUrl"
                :href="page.docsUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the product documentation -&gt;
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Story pages tell the problem and the coverage through their chapters, so they skip the
         hero stats, the problem cards and the module chips. -->
    <section v-if="!story" id="the-problem" class="section-light">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>{{ page.painTitle }}</h2>
        </div>

        <div class="pain-grid">
          <article v-for="pain in page.pains" :key="pain.title" class="pain-card">
            <span></span>
            <h3>{{ pain.title }}</h3>
            <p>{{ pain.copy }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- Story pages replace the screenshot showcase and the workflow list with their chapters. -->
    <div v-if="story" id="how-it-works">
      <LazyStoryFeatureStory :story="story" />
    </div>
    <section v-else id="the-product" class="showcase-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>See the workspace your clients feel.</h2>
        </div>

        <div class="showcase-list">
          <article
            v-for="(item, index) in showcaseItems"
            :key="item.title"
            class="showcase-row"
            :class="{ 'is-flipped': index % 2 === 1 }"
          >
            <div class="showcase-copy">
              <span>{{ String(index + 1).padStart(2, "0") }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.copy }}</p>
            </div>
            <div class="showcase-visual">
              <div class="seo-frame">
                <div class="seo-frame-bar" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                  <strong>{{ item.window }}</strong>
                </div>
                <img
                  :src="item.image"
                  :alt="item.alt"
                  width="1600"
                  :height="item.height"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="page.videoId" class="video-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>{{ page.videoTitle }}</h2>
        </div>

        <div class="video-frame">
          <template v-if="!videoPlaying">
            <img
              :src="`https://i.ytimg.com/vi/${page.videoId}/hqdefault.jpg`"
              :alt="page.videoTitle"
              width="480"
              height="360"
              loading="lazy"
              decoding="async"
            />
            <button type="button" class="video-play" aria-label="Play video" @click="videoPlaying = true">
              <span aria-hidden="true">▶</span>
            </button>
          </template>
          <iframe
            v-else
            :src="`https://www.youtube-nocookie.com/embed/${page.videoId}?autoplay=1&rel=0`"
            :title="page.videoTitle"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </section>

    <section v-if="!story" id="workflow" class="workflow-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading section-heading-dark">
          <h2>{{ page.workflowTitle }}</h2>
        </div>

        <div class="workflow-list">
          <article v-for="(step, index) in page.workflow" :key="step.title" class="workflow-step">
            <div class="step-number">{{ String(index + 1).padStart(2, "0") }}</div>
            <div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section v-if="!story && page.setupGuide" id="setup" class="section-light setup-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>{{ page.setupGuide.title }}</h2>
        </div>
        <p class="comparison-answer">{{ page.setupGuide.intro }}</p>
        <div class="setup-layout" :class="{ 'setup-layout--solo': !page.setupGuide.templates?.length }">
          <ol class="setup-steps">
            <li v-for="step in page.setupGuide.steps" :key="step.title">
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </li>
          </ol>
          <div v-if="page.setupGuide.templates?.length" class="setup-templates">
            <h3>{{ page.setupGuide.templatesTitle }}</h3>
            <figure v-for="item in page.setupGuide.templates" :key="item.label" class="setup-template">
              <figcaption>{{ item.label }}</figcaption>
              <blockquote>{{ item.text }}</blockquote>
              <p v-if="item.note">{{ item.note }}</p>
            </figure>
            <div v-if="page.setupGuide.planCta" class="setup-plan-cta">
              <p>{{ page.setupGuide.planCta.note }}</p>
              <a
                :href="addReferralToUrl(`https://app.workspace369.com/signup?plan=${page.setupGuide.planCta.plan}`)"
                data-conversion-event="start_free_click"
                :data-conversion-label="page.setupGuide.planCta.label"
                :data-conversion-location="`${page.slug}-setup`"
                :data-conversion-plan="page.setupGuide.planCta.plan"
              >{{ page.setupGuide.planCta.label }} <span aria-hidden="true">→</span></a>
              <TrialDisclosure :plan="page.setupGuide.planCta.plan" />
            </div>
          </div>
        </div>
        <p v-if="page.setupGuide.compliance" class="comparison-source setup-compliance">
          <template v-for="part in page.setupGuide.compliance" :key="part.text || part.href">
            <a v-if="part.href" :href="part.href" target="_blank" rel="noopener">{{ part.label }}</a>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <LazyMissedCallCostCalculator v-if="page.setupGuide.calculator" />
      </div>
    </section>

    <section v-if="!story && page.decision" id="options" class="section-light decision-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
        <div class="section-heading">
          <h2>{{ page.decision.title }}</h2>
        </div>
        <p class="comparison-answer">{{ page.decision.answer }}</p>
        <div class="comparison-scroll" role="region" :aria-label="page.decision.title" tabindex="0">
          <table class="comparison-table decision-table">
            <thead>
              <tr>
                <th v-for="column in page.decision.columns" :key="column" scope="col">{{ column }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in page.decision.rows" :key="row[0]">
                <th scope="row">{{ row[0] }}</th>
                <td v-for="(cell, i) in row.slice(1)" :key="i"><span class="decision-label">{{ page.decision.columns[i + 1] }}</span>{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="comparison-source">
          {{ page.decision.source }}
          <template v-for="(link, i) in page.decision.sources" :key="link.href">
            <a :href="link.href" target="_blank" rel="noopener">{{ link.label }}</a>{{ i < page.decision.sources.length - 1 ? ", " : "." }}
          </template>
        </p>
      </div>
    </section>

    <section v-if="!story" id="coverage" class="section-light">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="module-layout">
          <div class="module-copy">
            <h2>{{ page.modulesTitle }}</h2>
            <ul>
              <li v-for="item in page.proofBullets" :key="item">{{ item }}</li>
            </ul>
          </div>

          <div class="module-grid">
            <div v-for="module in page.modules" :key="module" class="module-chip">
              {{ module }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <TeamOfferSection v-if="teamOffer" :location="page.slug" />
    <LazyFreelancerOfferSection v-else-if="freelancerPlan" :location="page.slug" :featured="freelancerPlan" />

    <HomeReviews
      v-if="!story"
      eyebrow="Selected App Store reviews"
      title="What reviewers say about Workspace369."
      cta-label="Read more reviews"
      :location="`${page.slug}-reviews`"
    />

    <section v-if="!story" class="seo-cta-band" data-sticky-cta-hide>
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
        <div class="seo-cta-inner">
          <div class="seo-cta-copy">
            <h2>See Workspace369 on your own client work.</h2>
            <span v-if="teamOffer">
              Start your trial, invite the team and, for a limited time, claim free
              setup and migration help in your workspace. Prefer a walkthrough first? Book a demo.
            </span>
            <span v-else-if="freelancerPlan">
              Start your trial and send your first proposal or invoice from your own
              workspace. Compare plans any time; upgrade only when your work needs more.
            </span>
            <span v-else>
              Start a free trial or talk to sales — either way, you will see the
              whole loop: inbox, projects, portal, and billing.
            </span>
          </div>
          <div class="seo-cta-actions">
            <a
              :href="addReferralToUrl('https://app.workspace369.com/signup')"
              class="seo-cta-primary"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${page.badge} mid-page CTA`"
              :data-conversion-location="page.slug"
            >
              Start free trial
            </a>
            <TrialDisclosure on-dark />
            <NuxtLink
              v-if="freelancerPlan"
              :to="localePath('/pricing')"
              class="seo-cta-secondary"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${page.badge} mid-page pricing CTA`"
              :data-conversion-location="page.slug"
            >
              Compare plans
            </NuxtLink>
            <NuxtLink
              v-else
              :to="localePath('/contact-sales')"
              class="seo-cta-secondary"
              data-conversion-event="demo_request_click"
              :data-conversion-label="`${page.badge} mid-page sales CTA`"
              :data-conversion-location="page.slug"
            >
              {{ teamOffer ? "Book a demo" : "Talk to sales" }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section v-if="isFeaturePage && !story && page.featureScenarios?.length" class="feature-scenarios-band">
      <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>{{ page.scenariosTitle }}</h2>
        </div>

        <div class="feature-scenarios-grid">
          <article v-for="(scenario, index) in page.featureScenarios" :key="scenario.title">
            <span>{{ String(index + 1).padStart(2, "0") }}</span>
            <h3>{{ scenario.title }}</h3>
            <p>{{ scenario.copy }}</p>
          </article>
        </div>
      </div>
    </section>

    <section v-if="page.learningLinks?.length" class="feature-related-band">
      <div class="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h2 class="text-2xl font-bold">Put the workflow into practice</h2>
        <nav class="feature-related-grid" aria-label="Workflow guides and tools">
          <NuxtLink v-for="link in page.learningLinks" :key="link.path" :to="localePath(link.path)">{{ link.title }} <span aria-hidden="true">→</span></NuxtLink>
        </nav>
      </div>
    </section>

    <section v-if="isFeaturePage && page.relatedLinks?.length" class="feature-related-band">
      <div class="mx-auto max-w-6xl px-4 py-12 sm:px-6 md:py-16">
        <div class="feature-related-heading">
          <h2>See how this feature connects to the rest of the client workflow.</h2>
        </div>

        <nav class="feature-related-grid" aria-label="Related Workspace369 pages">
          <NuxtLink
            v-for="link in page.relatedLinks"
            :key="link.path"
            :to="localePath(link.path)"
          >
            <span>{{ link.label }}</span>
            <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </nav>
      </div>
    </section>

    <section id="faq" class="faq-section">
      <div class="mx-auto max-w-4xl px-4 py-14 sm:px-6 md:py-20">
        <div class="section-heading">
          <h2>{{ page.proofTitle }}</h2>
        </div>

        <div class="faq-list">
          <article v-for="item in page.faq" :key="item.q" class="faq-card">
            <h3>{{ item.q }}</h3>
            <p>{{ item.a }}</p>
          </article>
        </div>
      </div>
    </section>

    <RelatedPages
      v-if="!story"
      :path="page.slug"
      :title="page.h1"
      :exclude="(page.relatedLinks || []).map((link) => link.path)"
    />

    <FeatureReleaseUpdates :path="page.slug" />
    <Cta03 :title="story?.ctaTitle" :body="story?.ctaCopy" :plain="!!story" :signup-url="story ? storyTrialUrl(story) : ''" />
    <PhoneStickyCta
      :href="story ? storyTrialUrl(story) : 'https://app.workspace369.com/signup'"
      :plan="storyTrialPlan(story)"
      :label="page.badge"
      :location="page.slug"
      watch=".seo-hero .seo-actions"
    />
  </main>
</template>

<script setup>
import { localizedUrl } from "~/data/locales";
import pricingFeatures from "~/data/pricing-features.json";
import storyLoaders from "#build/page-data/stories-loaders.mjs";
import { storyTrialPlan, storyTrialUrl } from "~/data/story-trial-url";
import { isTeamOfferPage } from "~/data/team-offer-pages";
import { freelancerOfferPlan } from "~/data/freelancer-offer-pages";

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
});

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = "https://workspace369.com";
const canonicalUrl = computed(() => localizedUrl(siteUrl, props.page.slug, canonicalLocaleCode.value));
const isFeaturePage = computed(() => props.page.slug.startsWith("/features/"));
// Feature pages with a story entry render in story mode.
// Only that page's story module loads (modules/page-data.ts).
const storyData = isFeaturePage.value ? await storyLoaders[props.page.slug.replace("/features/", "")]?.() : null;
const story = computed(() => storyData || null);
const teamOffer = computed(() => isTeamOfferPage(props.page.slug));
const freelancerPlan = computed(() => freelancerOfferPlan(props.page.slug));

// The answer may carry in-sentence site links written as [label](/path); everything else is plain text.
const answerParts = computed(() => {
  const parts = [];
  const text = props.page.answerSummary || "";
  const linkPattern = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;
  let last = 0;
  for (const match of text.matchAll(linkPattern)) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: match[1], path: match[2] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
});

// The hero keeps the first sentences of the lede (up to three, about 340 characters) so the
// call to action stays in view; the rest opens the key facts band below, word for word.
// Story pages hide that band, so a story without heroCopy shows the whole lede in the hero.
const ledeParts = computed(() => {
  const sentences = (props.page.lede || "").split(/(?<=[.!?])\s+(?=[A-Z0-9"“])/);
  let hero = sentences[0] || "";
  let count = 1;
  while (count < sentences.length && count < 3 && hero.length + 1 + sentences[count].length <= 340) {
    hero += " " + sentences[count];
    count += 1;
  }
  return { hero, rest: sentences.slice(count).join(" ") };
});

const pageRoot = ref(null);
useTiltFrames(pageRoot);

const videoPlaying = ref(false);

// Hero visual per vertical: billing-led pages show the invoice editor,
// scheduling-led pages the calendar, communication-led pages the inbox,
// project-led pages the board, record-led pages the client profile. This is
// conditional content per page (not a noun swap) and differentiates the
// vertical heroes from each other.
const HERO_IMAGES = {
  dashboard: {
    src: "/images/solutions/dashboard-light-clean.webp",
    height: 859,
    alt: "Workspace369 Home: overdue invoices, proposals waiting on a reply, an AutoPilot briefing, revenue for the year and money coming in over the next 30 days",
  },
  inbox: {
    src: "/images/solutions/inbox-light-clean.webp",
    height: 1169,
    alt: "Workspace369 shared inbox showing email, SMS, and call history beside a client record",
  },
  projects: {
    src: "/images/solutions/projects-board-light-clean.webp",
    height: 859,
    alt: "Workspace369 project board showing client work organized into stages with owners and status",
  },
  invoice: {
    src: "/images/solutions/invoice-edit-light-clean.webp",
    height: 859,
    alt: "Workspace369 invoice editor showing line items, payment terms, and client details",
  },
  crm: {
    src: "/images/solutions/crm-light-clean.webp",
    height: 1169,
    alt: "Workspace369 client record showing history, files, communication, and invoices in one profile",
  },
  calendar: {
    src: "/images/solutions/calendar-month-light-clean.webp",
    height: 859,
    alt: "Workspace369 calendar showing scheduled client work, appointments, and reminders",
  },
};

const heroVisual = computed(() => HERO_IMAGES[props.page.heroImage] || HERO_IMAGES.dashboard);

// Citable, number-carrying sentences: per-page facts computed from the page's
// own data plus plan facts read from the canonical pricing-features.json
// (shared with the app), so the figures stay true when pricing changes.
const planFacts = computed(() => {
  const plans = pricingFeatures.plans || [];
  const cadet = plans.find((plan) => plan.slug === "cadet") || {};
  const top = plans.find((plan) => plan.slug === "commander") || {};
  // Plans show usage as a multiple of Cadet's, never token counts (as data/pricing-plans.ts does).
  const tokens = (plan) => Number(String(plan.aiTokens || "").replace(/[^0-9]/g, "")) || 0;
  const multiple = tokens(cadet) ? Math.round(tokens(top) / tokens(cadet)) : 1;
  return {
    cadetMonthly: cadet.monthly,
    cadetSeats: cadet.seats,
    topName: top.name,
    topSeats: top.seats,
    topStorage: top.storage,
    topUsage: multiple <= 1 ? "standard monthly usage" : `${multiple}× Cadet usage`,
    topMonthly: top.monthly,
  };
});

const pageFacts = computed(() => {
  const modules = props.page.modules || [];
  const steps = props.page.workflow || [];
  const lines = [];
  if (modules.length >= 4) {
    lines.push(
      `${props.page.badge} at Workspace369 covers ${modules.length} areas, including ${modules
        .slice(0, 3)
        .map((module) => module.charAt(0).toLowerCase() + module.slice(1))
        .join(", ")}.`,
    );
  }
  if (steps.length >= 2) {
    lines.push(
      `The workflow runs in ${steps.length} steps, from "${steps[0].title.toLowerCase()}" to "${steps[
        steps.length - 1
      ].title.toLowerCase()}".`,
    );
  }
  return lines;
});

// Product showcase: real app screens so every solution page shows the product,
// not just describes it. Each page leads with the screen that matches its own
// loop (billing, scheduling, communication, projects, records) so verticals
// present differently, and scheduling-led pages swap in the calendar.
const SHOWCASE_CANDIDATES = {
  inbox: {
    window: "Inbox",
    title: "Every channel in one inbox",
    copy: "Email lands beside the client record on every plan; texts, calls and voicemail join it from Voyager, with AI call summaries from Specialist.",
    image: "/images/solutions/inbox-light-clean.webp",
    height: 1169,
    alt: "Workspace369 shared inbox showing email, SMS, and call history beside a client record",
  },
  projects: {
    window: "Projects",
    title: "Client work, organized as projects",
    copy: "Tasks, files, and status for every engagement — visible to the team and, through the client portal, to the client.",
    image: "/images/solutions/projects-board-light-clean.webp",
    height: 859,
    alt: "Workspace369 project board showing client work organized into stages with owners and status",
  },
  invoice: {
    window: "Invoices",
    title: "Proposals to paid invoices",
    copy: "Send quotes, bill milestones or retainers, and let automated reminders chase anything outstanding.",
    image: "/images/solutions/invoice-edit-light-clean.webp",
    height: 859,
    alt: "Workspace369 invoice editor showing line items, payment terms, and client details",
  },
  crm: {
    window: "Clients",
    title: "A portal clients actually check",
    copy: "Clients watch progress, files, and invoices in a branded portal instead of asking your team for updates.",
    image: "/images/solutions/crm-light-clean.webp",
    height: 1169,
    alt: "Workspace369 client record showing history, files, communication, and invoices in one profile",
  },
  calendar: {
    window: "Calendar",
    title: "Scheduling that fills itself",
    copy: "Appointments, shoots, sessions, and follow-ups tied to the client record — with automated reminders arriving on time.",
    image: "/images/solutions/calendar-month-light-clean.webp",
    height: 859,
    alt: "Workspace369 calendar showing scheduled client work, appointments, and reminders",
  },
};

const SHOWCASE_ORDERS = {
  invoice: ["invoice", "inbox", "projects", "crm"],
  calendar: ["calendar", "inbox", "projects", "invoice"],
  inbox: ["inbox", "projects", "invoice", "crm"],
  projects: ["projects", "invoice", "inbox", "crm"],
  crm: ["crm", "inbox", "projects", "invoice"],
  dashboard: ["inbox", "projects", "invoice", "crm"],
};

const showcaseItems = computed(() => {
  const order = SHOWCASE_ORDERS[props.page.heroImage] || SHOWCASE_ORDERS.dashboard;
  const documentedWorkflow = [
    '/solutions/web-design-agency-software',
    '/solutions/seo-agency-management-software',
    '/solutions/photography-business-software',
    '/solutions/invoicing-software-for-consultants',
    '/solutions/freelance-business-software',
    '/solutions/freelancer-invoicing-software',
    '/solutions/crm-for-freelance-developers',
    '/solutions/consulting-firm-software',
    '/solutions/proposal-software-for-consultants',
  ].includes(props.page.slug);
  return order.map((key) => documentedWorkflow && key === 'invoice'
    ? { ...SHOWCASE_CANDIDATES.invoice, title: 'Review the work, then invoice', copy: 'Prepare the client invoice with line items, payment terms and the agreed amount. Review it before sending and track its payment status.' }
    : SHOWCASE_CANDIDATES[key]);
});

useHead(() => ({
  title: props.page.metaTitle,
  meta: [
    { name: "description", content: props.page.description },
    { name: "twitter:title", content: props.page.metaTitle },
    { name: "twitter:description", content: props.page.description },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: props.page.metaTitle },
    { property: "og:description", content: props.page.description },
    { property: "og:url", content: canonicalUrl.value },
  ],
  link: [{ key: "canonical", rel: "canonical", href: canonicalUrl.value }],
  script: [
    {
      key: `workspace369-solution-${props.page.slug}`,
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "@id": `${canonicalUrl.value}#service`,
            name: props.page.badge,
            provider: { "@id": `${siteUrl}/#organization` },
            serviceType: props.page.badge,
            url: canonicalUrl.value,
            description: props.page.description,
            areaServed: "US",
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${canonicalUrl.value}#breadcrumbs`,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: `${siteUrl}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: isFeaturePage.value ? "Features" : "Solutions",
                item: localizedUrl(
                  siteUrl,
                  isFeaturePage.value ? "/features" : "/solutions",
                  canonicalLocaleCode.value,
                ),
              },
              {
                "@type": "ListItem",
                position: 3,
                name: props.page.badge,
                item: canonicalUrl.value,
              },
            ],
          },
          {
            "@type": "FAQPage",
            "@id": `${canonicalUrl.value}#faq`,
            mainEntity: props.page.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          },
          ...(props.page.videoId
            ? [
                {
                  "@type": "VideoObject",
                  "@id": `${canonicalUrl.value}#video`,
                  name: props.page.videoTitle,
                  description: props.page.videoDescription || props.page.description,
                  thumbnailUrl: `https://i.ytimg.com/vi/${props.page.videoId}/hqdefault.jpg`,
                  uploadDate: props.page.dateModified || "2026-08-14",
                  embedUrl: `https://www.youtube-nocookie.com/embed/${props.page.videoId}`,
                  url: canonicalUrl.value,
                },
              ]
            : []),
        ],
      }),
    },
  ],
}));
</script>

<style scoped>
/* No content-visibility on these sections (unlike ComparisonLandingPage): Nuxt re-scrolls to #faq
   after hydration, and placeholder sections above the FAQ resizing then cost ~0.3 CLS on desktop. */
.seo-page {
  background: #fff;
}

.feature-definition-band {
  border-bottom: 1px solid #dbe4ee;
  background: #eef7fa;
}

.feature-definition-layout {
  display: grid;
  gap: 2.5rem;
}

.feature-definition-layout h2 {
  margin-top: 0.8rem;
  color: #020617;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 900;
  line-height: 1.04;
  text-wrap: balance;
}

.feature-definition-answer {
  color: #334155;
  font-size: 1.08rem;
  line-height: 1.76;
}

.feature-source-line {
  display: grid;
  gap: 0.45rem;
  margin-top: 1.6rem;
  border-top: 1px solid #bfd5df;
  padding-top: 1rem;
}

.feature-source-line span {
  color: #0f7792;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
}

.feature-source-line p,
.feature-source-line a {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.55;
}

.feature-source-line a {
  width: fit-content;
  color: #087ca0;
  font-weight: 850;
}

.feature-scenarios-band {
  border-bottom: 1px solid #dbe4ee;
  background: #f8fafc;
}

.feature-scenarios-grid {
  display: grid;
  gap: 1rem;
  margin-top: 2.5rem;
}

.feature-scenarios-grid article {
  border-top: 2px solid #1ba7ce;
  background: #ffffff;
  padding: 1.35rem 1.25rem 1.5rem;
}

.feature-scenarios-grid article > span {
  color: #0e7490;
  font-size: 0.7rem;
  font-weight: 900;
}

.feature-scenarios-grid h3 {
  margin-top: 1rem;
  color: #0f172a;
  font-size: 1.16rem;
  font-weight: 900;
  line-height: 1.3;
}

.feature-scenarios-grid article p {
  margin-top: 0.65rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.68;
}

.feature-related-band {
  border-bottom: 1px solid #dbe4ee;
  background: #ffffff;
}

.feature-related-heading {
  display: grid;
  max-width: 48rem;
  gap: 0.55rem;
}

.feature-related-heading h2 {
  color: #0f172a;
  font-size: clamp(1.55rem, 3vw, 2.35rem);
  font-weight: 900;
  line-height: 1.12;
}

.feature-related-grid {
  display: grid;
  margin-top: 2rem;
  border-top: 1px solid #dbe4ee;
}

.feature-related-grid a {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #dbe4ee;
  padding: 1rem 0;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 850;
}

.feature-related-grid a > span:last-child {
  color: #0e7490;
}

.feature-related-grid a:hover {
  color: #087ca0;
}

.seo-hero {
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 0%, rgb(27 167 206 / 0.13), transparent 30rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  color: #0f172a;
}

.dark .seo-hero {
  background:
    radial-gradient(circle at 12% 12%, rgb(27 167 206 / 0.18), transparent 28rem),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
  color: #fff;
}

.seo-hero-grid {
  display: grid;
  gap: 2.5rem;
  padding: 7rem 0 4rem;
  align-items: center;
  text-align: center;
}

.seo-hero-visual {
  position: relative;
  min-width: 0;
  margin-top: 1.5rem;
}

.seo-hero-story { max-width: 640px; margin-inline: auto; }
.seo-plan-note { margin: 0.75rem auto 0; max-width: 40rem; color: #475569; font-size: 0.9375rem; font-weight: 600; }
.dark .seo-plan-note { color: #cbd5e1; }
.seo-hero-story::before { display: none; }

.seo-hero-visual::before {
  position: absolute;
  inset: -22% -6%;
  background:
    radial-gradient(circle at 50% 18%, rgb(27 167 206 / 0.32), transparent 30rem),
    radial-gradient(circle at 12% 85%, rgb(14 116 144 / 0.2), transparent 22rem),
    radial-gradient(circle at 88% 80%, rgb(56 189 248 / 0.16), transparent 20rem);
  content: "";
  pointer-events: none;
}

.seo-frame {
  position: relative;
  overflow: hidden;
  border: 1px solid rgb(148 163 184 / 0.35);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 30px 80px rgb(2 6 23 / 0.16);
}

.seo-frame-bar {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0.55rem 0.8rem;
}

.seo-frame-bar span {
  height: 0.55rem;
  width: 0.55rem;
  border-radius: 999px;
  background: #cbd5e1;
}

.seo-frame-bar span:first-child {
  background: #fca5a5;
}

.seo-frame-bar span:nth-child(2) {
  background: #fcd34d;
}

.seo-frame-bar span:nth-child(3) {
  background: #86efac;
}

.seo-frame-bar strong {
  margin-left: 0.5rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
}

.seo-frame img {
  display: block;
  height: auto;
  width: 100%;
}

.showcase-band {
  background: #f8fafc;
}

.dark .showcase-band {
  background: #0a1220;
}

.video-band {
  background: #ffffff;
}

.dark .video-band {
  background: #020617;
}

.video-frame {
  position: relative;
  overflow: hidden;
  margin-top: 2.5rem;
  border: 1px solid rgb(148 163 184 / 0.35);
  border-radius: 10px;
  background: #020617;
  box-shadow: 0 30px 80px rgb(2 6 23 / 0.16);
  aspect-ratio: 16 / 9;
}

.video-frame img {
  display: block;
  height: 100%;
  width: 100%;
  object-fit: cover;
}

.video-frame iframe {
  display: block;
  height: 100%;
  width: 100%;
  border: 0;
}

.video-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(2 6 23 / 0.25);
  cursor: pointer;
  transition: background-color 160ms ease;
}

.video-play:hover {
  background: rgb(2 6 23 / 0.4);
}

.video-play span {
  display: flex;
  height: 4.25rem;
  width: 4.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #0e7490;
  color: #fff;
  font-size: 1.5rem;
  padding-left: 0.35rem;
  box-shadow: 0 16px 44px rgb(2 6 23 / 0.4);
}

.showcase-list {
  display: grid;
  gap: 3rem;
  margin-top: 2.5rem;
}

.showcase-row {
  display: grid;
  gap: 1.75rem;
  align-items: center;
}

.showcase-copy > span {
  color: #0e7490;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0;
}

.showcase-copy h3 {
  margin-top: 0.7rem;
  color: #0f172a;
  font-size: clamp(1.5rem, 2.6vw, 2.1rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.1;
}

.dark .showcase-copy h3 {
  color: #f8fafc;
}

.showcase-copy p {
  margin-top: 0.9rem;
  max-width: 30rem;
  color: #475569;
  font-size: 1rem;
  line-height: 1.7;
}

.dark .showcase-copy p {
  color: #94a3b8;
}

.showcase-visual {
  min-width: 0;
}

.seo-hero h1 {
  margin: 1.25rem auto 0;
  max-width: 52rem;
  color: #020617;
  font-size: clamp(2rem, 3.6vw, 3.35rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  overflow-wrap: anywhere;
  text-wrap: balance;
}

.dark .seo-hero h1 {
  color: #fff;
}

.seo-answer {
  margin: 1.4rem auto 0;
  max-width: 40rem;
  color: #0f172a;
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.7;
  letter-spacing: 0;
}

.dark .seo-answer {
  color: #fff;
}

.seo-answer a {
  color: inherit;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
}

.seo-lede {
  margin: 1.4rem auto 0;
  max-width: 40rem;
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.75;
  letter-spacing: 0;
}

.dark .seo-lede {
  color: #cbd5e1;
}

.seo-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
}

.seo-primary,
.seo-secondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  padding: 0.78rem 1.1rem;
  font-size: 0.92rem;
  font-weight: 800;
  /* A set line height keeps the button 3rem tall in the fallback and in Poppins (it grew 1px on the font swap: CLS). */
  line-height: 1.25rem;
  letter-spacing: 0;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.seo-primary {
  background: #0e7490;
  color: #fff;
}

.seo-primary:hover {
  background: #155e75;
}

.seo-secondary {
  border: 1px solid #cbd5e1;
  color: #0f172a;
}

.dark .seo-secondary {
  border-color: rgb(255 255 255 / 0.18);
  color: #fff;
}

.seo-secondary:hover {
  border-color: #94a3b8;
  background: rgb(15 23 42 / 0.04);
}

.dark .seo-secondary:hover {
  border-color: rgb(255 255 255 / 0.4);
  background: rgb(255 255 255 / 0.06);
}

.seo-stats {
  display: grid;
  gap: 0.7rem;
  margin: 2rem auto 0;
  max-width: 42rem;
  text-align: left;
}

.seo-stats div {
  min-width: 0;
  border-top: 1px solid #dbe4ee;
  padding: 0.85rem 0 0;
}

.dark .seo-stats div {
  border-color: rgb(148 163 184 / 0.24);
}

.seo-stats strong,
.seo-stats span {
  display: block;
  letter-spacing: 0;
}

.seo-stats strong {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 900;
}

.dark .seo-stats strong {
  color: #fff;
}

.seo-stats span {
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.35;
}

.dark .seo-stats span {
  color: #94a3b8;
}

.section-light {
  background: #fff;
}

.section-heading {
  max-width: 44rem;
}

.section-heading h2 {
  color: #0f172a;
  font-size: clamp(1.9rem, 3.4vw, 3rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.05;
}

.section-heading-dark h2 {
  color: #fff;
}

.pain-grid {
  display: grid;
  gap: 1rem;
  margin-top: 2.5rem;
}

.pain-card {
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
  background: #ffffff;
  box-shadow: 0 10px 30px rgb(15 23 42 / 0.05);
  padding: 1.25rem;
}

.pain-card span {
  display: block;
  height: 0.35rem;
  width: 2.25rem;
  border-radius: 2px;
  background: #1ba7ce;
}

.pain-card h3,
.workflow-step h3,
.faq-card h3 {
  margin-top: 1rem;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.25;
}

.pain-card p,
.workflow-step p,
.faq-card p {
  margin-top: 0.65rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.65;
  letter-spacing: 0;
}

.workflow-band {
  background: #0f172a;
}

.workflow-list {
  display: grid;
  gap: 0.9rem;
  margin-top: 2.5rem;
}

.workflow-step {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  min-width: 0;
  border: 1px solid rgb(148 163 184 / 0.16);
  border-radius: 0.55rem;
  background: rgb(15 23 42 / 0.78);
  padding: 1rem;
}

.workflow-step h3 {
  margin-top: 0;
  color: #fff;
}

.workflow-step p {
  color: #cbd5e1;
}

.step-number {
  display: flex;
  height: 2.6rem;
  width: 2.6rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  background: rgb(27 167 206 / 0.18);
  color: #a5f3fc;
  font-size: 0.78rem;
  font-weight: 900;
}

/* Phone grids here use implicit auto columns, so one long word sets the column's minimum width:
   German compounds ("Aufnahmeformularen" at heading size) widened the column past the screen and
   the page clipped the text. Words may now break, and headings hyphenate outside English. */
.seo-page :is(h1, h2, h3, h4, p, li, dt, dd, summary) {
  overflow-wrap: anywhere;
}

.seo-page :is(h1, h2, h3, h4):not(:lang(en)) {
  hyphens: auto;
}

.module-layout {
  display: grid;
  gap: 2rem;
  align-items: start;
}

.module-copy h2 {
  margin-top: 1rem;
  color: #0f172a;
  font-size: clamp(1.9rem, 3.4vw, 3rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.05;
}

.module-copy ul {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.5rem;
}

.module-copy li {
  position: relative;
  padding-left: 1.25rem;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.65;
}

.module-copy li::before {
  position: absolute;
  top: 0.7rem;
  left: 0;
  height: 0.42rem;
  width: 0.42rem;
  border-radius: 999px;
  background: #1ba7ce;
  content: "";
}

.module-grid {
  display: grid;
  gap: 0.7rem;
}

.module-chip {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.55rem;
  border: 1px solid #dbe7f0;
  border-radius: 0.45rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 0.9rem 1rem;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 850;
  letter-spacing: 0;
}

.module-chip::before {
  display: inline-flex;
  flex-shrink: 0;
  height: 1.15rem;
  width: 1.15rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgb(27 167 206 / 0.14);
  color: #0e7490;
  content: "✓";
  font-size: 0.68rem;
  font-weight: 900;
}

.seo-cta-band {
  background:
    radial-gradient(circle at 88% 12%, rgb(27 167 206 / 0.22), transparent 26rem),
    linear-gradient(180deg, #0b1c33 0%, #020617 100%);
}

.seo-cta-inner {
  display: grid;
  gap: 2rem;
  align-items: center;
}

.seo-cta-copy h2 {
  max-width: 34rem;
  color: #ffffff;
  font-size: clamp(1.9rem, 3.4vw, 2.75rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.06;
}

.seo-cta-copy span {
  display: block;
  margin-top: 0.9rem;
  max-width: 32rem;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.7;
}

.seo-cta-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.seo-actions :deep(.trial-disclosure),
.seo-cta-actions :deep(.trial-disclosure) {
  margin-top: -0.2rem;
  margin-bottom: 0.2rem;
}

.seo-cta-primary,
.seo-cta-secondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  padding: 0.78rem 1.35rem;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: 0;
  transition: background-color 160ms ease, border-color 160ms ease;
}

.seo-cta-primary {
  background: #0e7490;
  color: #fff;
}

.seo-cta-primary:hover {
  background: #155e75;
}

.seo-cta-secondary {
  border: 1px solid rgb(255 255 255 / 0.22);
  color: #fff;
}

.seo-cta-secondary:hover {
  border-color: rgb(255 255 255 / 0.45);
  background: rgb(255 255 255 / 0.06);
}

.faq-section {
  background: #f8fafc;
}

.faq-list {
  display: grid;
  gap: 0.85rem;
  margin-top: 2.5rem;
}

.faq-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
  background: #fff;
  padding: 1.15rem;
}

.faq-card h3 {
  margin-top: 0;
}

/* Hover polish: subtle lift + inner zoom on product frames, gentle lift on cards */
.seo-frame,
.pain-card,
.module-chip,
.workflow-step,
.faq-card {
  transition:
    transform 190ms ease,
    box-shadow 190ms ease,
    border-color 190ms ease,
    background-color 190ms ease;
}

.seo-frame img {
  transition: transform 260ms ease;
}

.seo-frame:hover {
  transform: translateY(-4px);
  box-shadow: 0 40px 100px rgb(2 6 23 / 0.22);
}

.seo-frame:hover img {
  transform: scale(1.012);
}

.seo-frame.tilt-enabled {
  transition:
    box-shadow 190ms ease,
    border-color 190ms ease,
    background-color 190ms ease;
}

.seo-frame.is-tilting {
  transform: perspective(1100px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) translateY(-4px);
  box-shadow: 0 40px 100px rgb(2 6 23 / 0.22);
}

.seo-frame.is-tilting img {
  transform: scale(1.012);
}

.seo-frame::after {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    560px circle at var(--mx, 50%) var(--my, 50%),
    rgb(255 255 255 / 0.1),
    transparent 42%
  );
  content: "";
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease;
}

.seo-frame.is-tilting::after {
  opacity: 1;
}

.pain-card:hover,
.faq-card:hover {
  transform: translateY(-3px);
  border-color: rgb(27 167 206 / 0.4);
  box-shadow: 0 16px 44px rgb(15 23 42 / 0.09);
}

.module-chip:hover {
  transform: translateY(-2px);
  border-color: rgb(27 167 206 / 0.5);
}

.workflow-step:hover {
  transform: translateY(-2px);
  border-color: rgb(103 232 249 / 0.4);
}

.facts-band {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.dark .facts-band {
  border-color: #243247;
  background: #0a1220;
}

.facts-intro {
  max-width: 48rem;
  margin-bottom: 1.75rem;
  color: #334155;
  font-size: 1.05rem;
  line-height: 1.75;
}

.dark .facts-intro {
  color: #cbd5e1;
}

.facts-eyebrow {
  color: #0e7490;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.dark .facts-eyebrow {
  color: #67e8f9;
}

.facts-grid {
  display: grid;
  gap: 0.9rem 2.5rem;
  margin-top: 1.25rem;
}

.fact-line {
  border-left: 2px solid rgb(27 167 206 / 0.5);
  padding-left: 0.85rem;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.65;
}

.dark .fact-line {
  color: #cbd5e1;
}

.fact-line a {
  color: #0e7490;
  font-weight: 800;
}

.dark .fact-line a {
  color: #67e8f9;
}

.page-nav {
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.dark .page-nav {
  border-color: #243247;
  background: #020617;
}

.page-nav-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.7rem 0;
  scrollbar-width: none;
}

.page-nav-inner::-webkit-scrollbar {
  display: none;
}

.page-nav-inner > span {
  flex: none;
  margin-right: 0.5rem;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.dark .page-nav-inner > span {
  color: #94a3b8;
}

.page-nav-inner a {
  flex: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.35rem 0.85rem;
  color: #334155;
  font-size: 0.8rem;
  font-weight: 750;
  letter-spacing: 0;
  transition:
    border-color 160ms ease,
    color 160ms ease,
    background-color 160ms ease;
}

.page-nav-inner a:hover {
  border-color: rgb(27 167 206 / 0.5);
  background: rgb(27 167 206 / 0.06);
  color: #0e7490;
}

.dark .page-nav-inner a {
  border-color: #243247;
  color: #cbd5e1;
}

.dark .page-nav-inner a:hover {
  border-color: rgb(103 232 249 / 0.45);
  background: rgb(103 232 249 / 0.08);
  color: #67e8f9;
}

#the-problem,
#the-product,
#workflow,
#coverage,
#faq,
#what-is {
  scroll-margin-top: 5rem;
}

@media (prefers-reduced-motion: reduce) {
  .seo-frame,
  .seo-frame img,
  .pain-card,
  .module-chip,
  .workflow-step,
  .faq-card {
    transition: none;
  }

  .seo-frame:hover,
  .seo-frame.is-tilting,
  .seo-frame:hover img,
  .seo-frame.is-tilting img,
  .pain-card:hover,
  .module-chip:hover,
  .workflow-step:hover,
  .faq-card:hover {
    transform: none;
  }
}

.comparison-answer {
  max-width: 48rem;
  margin-top: 1.25rem;
  color: #334155;
  font-size: 1.05rem;
  line-height: 1.65;
}

.comparison-scroll {
  margin-top: 2rem;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
}

.comparison-scroll:focus-visible {
  outline: 2px solid #0e7490;
  outline-offset: 2px;
}

/* Sideways scrollers (comparison table, in-page nav) had no cue on phones, where the scrollbar is
   hidden: fade whichever edge still has content past it. Driven by the scroller's own position, so a
   row that fits gets no fade, and browsers without scroll timelines keep the plain edge. */
@property --fade-start {
  syntax: "<length>";
  inherits: false;
  initial-value: 0px;
}

@property --fade-end {
  syntax: "<length>";
  inherits: false;
  initial-value: 0px;
}

@keyframes scroll-edge-fade {
  0% { --fade-start: 0px; --fade-end: 2.5rem; }
  6% { --fade-start: 2.5rem; }
  94% { --fade-end: 2.5rem; }
  100% { --fade-start: 2.5rem; --fade-end: 0px; }
}

@supports (animation-timeline: scroll()) {
  .comparison-scroll,
  .page-nav-inner {
    -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--fade-start), #000 calc(100% - var(--fade-end)), transparent);
    mask-image: linear-gradient(to right, transparent, #000 var(--fade-start), #000 calc(100% - var(--fade-end)), transparent);
    animation: scroll-edge-fade linear both;
    animation-timeline: scroll(self inline);
  }

  [dir="rtl"] .comparison-scroll,
  [dir="rtl"] .page-nav-inner {
    -webkit-mask-image: linear-gradient(to left, transparent, #000 var(--fade-start), #000 calc(100% - var(--fade-end)), transparent);
    mask-image: linear-gradient(to left, transparent, #000 var(--fade-start), #000 calc(100% - var(--fade-end)), transparent);
  }

  /* A mask also clips the focus ring drawn outside the box. */
  .comparison-scroll:focus-visible {
    -webkit-mask-image: none;
    mask-image: none;
  }
}

.comparison-table {
  width: 100%;
  min-width: 44rem;
  border-collapse: collapse;
  font-size: 0.92rem;
  text-align: start;
}

.comparison-table th,
.comparison-table td {
  padding: 0.75rem 0.9rem;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  text-align: start;
  vertical-align: top;
}

.comparison-table thead th {
  background: #f8fafc;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 800;
}

.comparison-table tbody th {
  color: #0f172a;
  font-weight: 800;
  white-space: nowrap;
}

.comparison-table tbody tr:last-child th,
.comparison-table tbody tr:last-child td {
  border-bottom: 0;
}

.comparison-ours {
  background: #ecfeff;
}

.comparison-source {
  max-width: 48rem;
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.6;
}

.comparison-source a {
  color: #0e7490;
  font-weight: 700;
  text-decoration: underline;
}

/* Setup guide: numbered steps beside the copy-ready texts, then the compliance line and the calculator. */
.setup-layout {
  display: grid;
  gap: 2rem;
  margin-top: 2rem;
}

.setup-steps {
  display: grid;
  gap: 1.25rem;
  counter-reset: setup-step;
}

.setup-steps li {
  position: relative;
  padding-inline-start: 2.75rem;
  counter-increment: setup-step;
}

.setup-steps li::before {
  content: counter(setup-step);
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  display: grid;
  place-items: center;
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 0.375rem;
  background: #ecfeff;
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 800;
}

.setup-steps h3,
.setup-templates h3 {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
}

.setup-steps p {
  margin-top: 0.3rem;
  color: #334155;
  line-height: 1.6;
}

.setup-templates {
  display: grid;
  gap: 0.85rem;
  align-content: start;
}

.setup-template {
  margin: 0;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
  background: #f8fafc;
}

.setup-plan-cta p {
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.55;
}

.setup-plan-cta a {
  display: inline-block;
  margin-top: 0.4rem;
  color: #0e7490;
  font-weight: 800;
}

.setup-template figcaption {
  color: #0e7490;
  font-size: 0.78rem;
  font-weight: 800;
}

.setup-template blockquote {
  margin: 0.4rem 0 0;
  color: #0f172a;
  font-size: 0.98rem;
  line-height: 1.55;
}

.setup-template p {
  margin-top: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1.5;
}

.setup-compliance {
  margin-top: 1.75rem;
}

.decision-band {
  border-top: 1px solid #e2e8f0;
}

.decision-table {
  min-width: 66rem;
}

.decision-table tbody th {
  min-width: 10rem;
  white-space: normal;
}

.decision-label {
  display: none;
}

/* Phones: one card per option instead of a 7-column table, each cell with its column name. */
@media (max-width: 640px) {
  .decision-band .comparison-scroll {
    overflow: visible;
    border: 0;
    -webkit-mask-image: none;
    mask-image: none;
    animation: none;
  }

  .decision-table,
  .decision-table tbody,
  .decision-table tr,
  .decision-table th,
  .decision-table td {
    display: block;
    min-width: 0;
  }

  .decision-table thead {
    display: none;
  }

  .decision-table tbody tr {
    margin-bottom: 0.85rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.55rem;
  }

  .decision-table tbody th {
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    font-size: 1rem;
  }

  .decision-table td {
    padding-block: 0.6rem;
  }

  .decision-table tbody tr th,
  .decision-table tbody tr:last-child th {
    border-bottom: 1px solid #e2e8f0;
  }

  .decision-table tbody tr td:last-child {
    border-bottom: 0;
  }

  .decision-label {
    display: block;
    margin-bottom: 0.15rem;
    color: #0e7490;
    font-size: 0.75rem;
    font-weight: 800;
  }

  .dark .decision-table tbody tr,
  .dark .decision-table tbody th {
    border-color: #243247;
  }

  .dark .decision-table tbody th {
    background: #0f172a;
  }

  .dark .decision-label {
    color: #67e8f9;
  }
}

@media (min-width: 900px) {
  .setup-layout {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 3rem;
  }

  /* Steps only (no copy-ready texts): one readable column. */
  .setup-layout--solo {
    grid-template-columns: minmax(0, 46rem);
  }
}

.dark .setup-steps h3,
.dark .setup-templates h3,
.dark .setup-template blockquote {
  color: #f8fafc;
}

.dark .setup-steps p {
  color: #cbd5e1;
}

.dark .setup-steps li::before {
  background: #0c2a33;
  color: #67e8f9;
}

.dark .setup-template {
  border-color: #243247;
  background: #0f172a;
}

.dark .setup-plan-cta p { color: #cbd5e1; }
.dark .setup-plan-cta a { color: #67e8f9; }

.dark .setup-template figcaption {
  color: #67e8f9;
}

.dark .setup-template p {
  color: #94a3b8;
}

.dark .decision-band {
  border-color: #243247;
}

.dark .comparison-answer,
.dark .comparison-table td {
  color: #cbd5e1;
}

.dark .comparison-scroll,
.dark .comparison-table th,
.dark .comparison-table td {
  border-color: #243247;
}

.dark .comparison-table thead th {
  background: #0f172a;
  color: #f8fafc;
}

.dark .comparison-table tbody th {
  color: #f8fafc;
}

.dark .comparison-ours {
  background: #0c2a33;
}

.dark .comparison-source {
  color: #94a3b8;
}

.dark .comparison-source a {
  color: #67e8f9;
}

/* Dark theme for body sections */
.dark .section-light,
.dark .feature-related-band {
  background: #020617;
}

.dark .faq-section,
.dark .feature-scenarios-band {
  background: #0a1220;
}

.dark .feature-definition-band {
  border-color: #243247;
  background: #0d1b2d;
}

.dark .feature-scenarios-band,
.dark .feature-related-band {
  border-color: #243247;
}

.dark .section-heading h2,
.dark .module-copy h2,
.dark .feature-related-heading h2,
.dark .pain-card h3,
.dark .faq-card h3,
.dark .feature-scenarios-grid h3,
.dark .feature-definition-layout h2 {
  color: #f8fafc;
}

.dark .pain-card p,
.dark .faq-card p,
.dark .module-copy li,
.dark .feature-scenarios-grid p,
.dark .feature-definition-answer {
  color: #94a3b8;
}

.dark .pain-card {
  border-color: #243247;
  background: #0f172a;
  box-shadow: 0 12px 36px rgb(0 0 0 / 0.25);
}

.dark .module-chip {
  border-color: #243247;
  background: linear-gradient(180deg, #0f172a 0%, #0a1220 100%);
  color: #e2e8f0;
}

.dark .faq-card {
  border-color: #243247;
  background: #0f172a;
}

.dark .feature-scenarios-grid article {
  border-color: #243247;
  background: #07101f;
}

.dark .feature-source-line {
  border-top-color: #243247;
}

.dark .feature-source-line span {
  color: #67e8f9;
}

.dark .feature-source-line p {
  color: #94a3b8;
}

.dark .feature-source-line a {
  color: #67e8f9;
}

.dark .feature-related-grid a {
  border-bottom-color: #243247;
  color: #e2e8f0;
}

.dark .feature-related-grid a:hover {
  color: #67e8f9;
}

@media (min-width: 540px) {
  .seo-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .seo-cta-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }

  /* The terms take a line of their own under the buttons. Their 34rem cap would let them fit beside the
     buttons, so the hero lifts it and keeps the measure with padding instead. */
  .seo-actions :deep(.trial-disclosure),
  .seo-cta-actions :deep(.trial-disclosure) {
    order: 3;
    flex-basis: 100%;
    margin: 0;
  }

  .seo-actions :deep(.trial-disclosure) {
    max-width: none;
    padding-inline: max(0px, calc((100% - 34rem) / 2));
  }

  .seo-primary,
  .seo-secondary {
    min-width: 10rem;
  }

  .seo-stats,
  .module-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 768px) {
  .seo-hero-grid {
    padding: 8rem 0 4.75rem;
  }

  .facts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .seo-cta-inner {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 3rem;
  }

  /* The actions column is sized by its content; the cap keeps the terms from widening it into the heading's space. */
  .seo-cta-actions {
    max-width: 34rem;
  }

  .pain-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .workflow-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feature-scenarios-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .feature-related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 2rem;
  }
}

@media (min-width: 1024px) {
  .showcase-row {
    grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.22fr);
    gap: 3.5rem;
  }

  .showcase-row.is-flipped {
    grid-template-columns: minmax(0, 1.22fr) minmax(0, 0.78fr);
  }

  .showcase-row.is-flipped .showcase-copy {
    order: 2;
  }

  .showcase-row.is-flipped .showcase-visual {
    order: 1;
  }

  .seo-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-width: 42rem;
  }

  .module-layout {
    grid-template-columns: minmax(0, 0.86fr) minmax(0, 1.14fr);
    gap: 3rem;
  }

  .feature-definition-layout {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
    gap: 4rem;
  }

  .module-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .seo-hero-grid {
    padding: 6.25rem 0 3.25rem;
  }

  .seo-hero h1 {
    font-size: 1.8rem;
    line-height: 1.12;
  }

  .pain-grid,
  .workflow-list,
  .module-grid,
  .faq-list {
    gap: 1.2rem;
  }
}

.dark .showcase-copy > span { color: #67e8f9; }
/* Story pages, v3 (whiteboard, Oct 22), matching FeatureDetailPage's story template: the hero
   stacks a short headline over centred actions with the product large under them and no stage
   box, section headings drop to one calmer scale, and the definition reads as a quiet note. */
.is-story .seo-hero { background: linear-gradient(#ffffff 0%, #f6f9fc 100%); }
.dark .is-story .seo-hero { background: linear-gradient(#020617 0%, #07101f 100%); }
.is-story .seo-hero h1 { font-size: clamp(2.4rem, 4.6vw, 3.9rem); font-weight: 760; letter-spacing: -0.032em; line-height: 1.04; }
.is-story .seo-hero-story { max-width: 50rem; margin-top: 1rem; }
.is-story .seo-hero-story :deep(.sst) { background: transparent; }
.is-story .seo-hero-story :deep(.sst::before) { display: none; }
@media (min-width: 900px) { .is-story .seo-hero-story { zoom: 1.16; } }
@media (min-width: 1200px) { .is-story .seo-hero-story { zoom: 1.3; } }
/* An app-screen hero is drawn at the app's own scale, so it gets width instead of a zoom: it breaks
   out of the 72rem text column to 78rem (90rem from 1600px), centred on the page. */
.is-story .seo-hero-story.seo-hero-story--app { --app-w: min(78rem, 100vw - 3rem); zoom: 1; width: var(--app-w); max-width: none; margin: 2.25rem calc(50% - var(--app-w) / 2) 1rem; }
@media (min-width: 1600px) { .is-story .seo-hero-story.seo-hero-story--app { --app-w: min(90rem, 100vw - 8rem); } }
/* With an app hero the copy runs wider and calmer (as FeatureDetailPage does) so more of the app clears the fold. */
@media (min-width: 761px) {
  .is-story .seo-hero-grid:has(.seo-hero-story--app) { gap: 1rem; }
  .is-story .seo-hero:has(.seo-hero-story--app) h1 { max-width: 66rem; font-size: clamp(2.3rem, 3.8vw, 3.25rem); }
  .is-story .seo-hero:has(.seo-hero-story--app) .seo-lede { max-width: 52rem; margin-top: 1rem; }
  .is-story .seo-hero:has(.seo-hero-story--app) .seo-plan-note { max-width: 52rem; }
  .is-story .seo-hero-story.seo-hero-story--app { margin-top: 1rem; }
}
.is-story :deep(section h2) { font-size: clamp(1.9rem, 3.1vw, 2.65rem); font-weight: 740; letter-spacing: -0.026em; line-height: 1.08; }
.is-story .feature-definition-band { border-top: 1px solid #e2e8f0; border-bottom: 0; background: #ffffff; }
.dark .is-story .feature-definition-band { border-color: #1e293b; background: #020617; }
.is-story .feature-definition-layout { grid-template-columns: minmax(0, 1fr); max-width: 52rem; gap: 1rem; }
.is-story .feature-definition-layout h2 { font-size: clamp(1.45rem, 2.2vw, 1.8rem); font-weight: 700; letter-spacing: -0.015em; }
@media (max-width: 760px) {
  .is-story .seo-hero-grid { text-align: left; }
  .is-story .seo-hero h1, .is-story .seo-lede, .is-story .seo-plan-note { margin-inline: 0; }
  .is-story .seo-actions { justify-content: flex-start; }
  .is-story .seo-hero :deep(.trial-disclosure) { margin-inline: 0; padding-inline: 0; max-width: 34rem; text-align: left; }
  .is-story .seo-hero h1 { font-size: 2.1rem; }
  /* The product comes right after the headline so it shows on the first screen (as on
     FeatureDetailPage); the promise, plan line and actions follow it. */
  .is-story .seo-hero-grid { display: flex; flex-direction: column; align-items: stretch; gap: 0; padding-top: 7.1rem; }
  .is-story .seo-hero-grid > .min-w-0 { display: contents; }
  .is-story .seo-hero-story { order: 1; margin: 0.6rem 0 0.4rem; }
  .is-story .seo-hero-story.seo-hero-story--app { width: auto; margin: 0.6rem 0 2.6rem; }
  .is-story .seo-lede,
  .is-story .seo-plan-note,
  .is-story .seo-actions { order: 2; }
}
</style>
